import * as RadixRadio from '@radix-ui/react-radio-group';
import { useId, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import s from './RadioGroup.module.css';

export type RadioOption = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

export type RadioGroupProps = Omit<RadixRadio.RadioGroupProps, 'children'> & {
  options: RadioOption[];
  /** Group label (rendered as a visible legend unless hideLabel). */
  label: ReactNode;
  hideLabel?: boolean;
  /** `card`: each option is a big tappable card (e.g. "Everyone in the family" / "Pick people"). */
  variant?: 'list' | 'card';
};

export function RadioGroup({
  options,
  label,
  hideLabel,
  variant = 'list',
  className,
  ...rest
}: RadioGroupProps) {
  const id = useId();
  return (
    <div className={cx(s.wrap, className)}>
      <p id={`${id}-label`} className={s.legend} data-hidden={hideLabel || undefined}>
        {label}
      </p>
      <RadixRadio.Root
        className={s.group}
        data-variant={variant}
        aria-labelledby={`${id}-label`}
        {...rest}
      >
        {options.map((o) => {
          const itemId = `${id}-${o.value}`;
          return (
            <label
              key={o.value}
              htmlFor={itemId}
              className={s.option}
              data-disabled={o.disabled || undefined}
            >
              <RadixRadio.Item
                id={itemId}
                value={o.value}
                disabled={o.disabled}
                className={s.radio}
              >
                <RadixRadio.Indicator className={s.dot} />
              </RadixRadio.Item>
              <span className={s.text}>
                <span className={s.label}>{o.label}</span>
                {o.description ? <span className={s.description}>{o.description}</span> : null}
              </span>
            </label>
          );
        })}
      </RadixRadio.Root>
    </div>
  );
}
